import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button, Title, DetailsModal, ValidationMessage, ModalWindow } from 'components';
import { PhaseResult, TestResultDetails } from 'pages/user-test/store';
import TestAnswersStatistic from '../../../../../components/TestAnswersStatistic/TestAnswersStatistic';
import { UserTestStatistic } from '../../../../../models/test/user-test-statistic';
import { CertDirection } from 'enums/cert-direction';

interface InfoResultProps {
    isSuccess: boolean;
    clearStore: () => void;
    title: string;
    testResultDetails?: TestResultDetails;
    testStatistic?: UserTestStatistic;
    isGiveCertificate: boolean;
    certDirection: CertDirection;
    passingTestId?: number;
    isStudentStudyCenter?: boolean;
    clearTest: () => void;
    getStudyGroup: () => void;
    attemptLeft: number;
    phases?: PhaseResult[];
}

export const InfoResult = (props: InfoResultProps) => {
    const {
        isSuccess,
        clearStore,
        testResultDetails,
        testStatistic,
        title,
        isGiveCertificate,
        passingTestId,
        isStudentStudyCenter,
        certDirection,
        clearTest,
        getStudyGroup,
        attemptLeft,
        phases,
    } = props;
    const { push } = useHistory();
    const { state } = useLocation<{ isTimeExpired: boolean }>();
    const [t] = useTranslation(['user-test']);
    const [modalIsOpen, setModalIsOpen] = useState(false);

    const handleBackToMenu = () => {
        clearStore();
        isStudentStudyCenter ? push('/') : push('/test');
    };

    const getBackButton = () => (
        <div className="link result-link" onClick={handleBackToMenu}>
            ← {isStudentStudyCenter ? t('translation:go-to-main') : t('back-to-menu')}
        </div>
    );

    return (
        <div className="test-result-information">
            {state?.isTimeExpired && (
                <div className="time-passed-message">
                    <ValidationMessage>{t('end-time-message')}</ValidationMessage>
                </div>
            )}
            {(isStudentStudyCenter || certDirection === CertDirection.ProductLK) && testStatistic && (
                <ModalWindow onCloseClick={() => setModalIsOpen(false)} isOpen={modalIsOpen} theme="modified-darkened">
                    <TestAnswersStatistic statistic={testStatistic} showDownload={false} showEdit={false} />
                </ModalWindow>
            )}
            {isSuccess ? (
                <>
                    {!isStudentStudyCenter && testResultDetails && (
                        <DetailsModal
                            additionalClasses="test-result-information-modal"
                            details={testResultDetails}
                            isOpen={modalIsOpen}
                            handleCloseClick={() => setModalIsOpen(false)}
                            title={t('modal-title')}
                            theme="modified-darkened"
                        />
                    )}
                    <Title titleText={title} />
                    {!isGiveCertificate && getBackButton()}
                    <p className="result-text">{t('highest-result-message')}</p>
                    <div className="buttons-row">
                        <Button buttonClass="primary new-design-button" handleClick={() => setModalIsOpen(true)}>
                            {t('show-details-results')}
                        </Button>

                        {isGiveCertificate && (!phases || phases.length === 0) && (
                            <Button
                                buttonClass="primary new-design-button"
                                handleClick={() => {
                                    clearStore();
                                    push('/certificates/confirm-data', { passingTestId });
                                }}
                            >
                                {t('get-certificate')}
                            </Button>
                        )}
                        {phases?.length !== 0 && (
                            <ol className="phase-result-container">
                                {t('need-to-get-approve')}
                                {phases?.map((phase) => (
                                    <li className="phase-result-item" key={phase.id}>
                                        {phase.description}
                                    </li>
                                ))}
                            </ol>
                        )}
                    </div>
                </>
            ) : (
                <>
                    {getBackButton()}

                    <p className="result-text">{t('failed-result-message')}</p>

                    {(isStudentStudyCenter || certDirection === CertDirection.ProductLK) && (
                        <div className="buttons-row">
                            <Button buttonClass="primary new-design-button" handleClick={() => setModalIsOpen(true)}>
                                {t('show-details-results')}
                            </Button>
                        </div>
                    )}

                    {!isStudentStudyCenter && attemptLeft > 0 && (
                        <Button
                            buttonClass="primary try-button"
                            handleClick={async () => {
                                await clearTest();
                                push('/test');
                                await getStudyGroup();
                            }}
                        >
                            {t('try-again')}
                        </Button>
                    )}
                </>
            )}
        </div>
    );
};
